import React, { useEffect, useMemo, useRef, useState } from "react";

import "./BitcoinNavigatorPaywall.scss";
import { Modal } from "@material-ui/core";
import { Button } from "@mui/material";
import { connect } from "react-redux";
import moment from "moment";
import { useHistory } from "react-router-dom";

import { useAuth } from "../../contexts/Auth";
import BeautifulSkeleton from "../common/Skeletons/BeautifulSkeleton";
import ContactForm from "../EmeraldSignup/ContactForm";
import {
  BN_PAYWALL_BODY,
  BN_PAYWALL_DISCLAIMER,
  BN_PAYWALL_FEATURES,
  BN_PAYWALL_HEADLINE,
  BNP,
  BNP_PREVIEW_ALLOCATION,
} from "./constants";
import BitcoinNavigatorWordmark from "./BitcoinNavigatorWordmark";

/* Scroll-reveal without framer-motion: IntersectionObserver toggles a class;
 * prefers-reduced-motion renders everything visible via CSS. */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-40px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`bn-reveal ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

/* Feature icons (primary-blue per brand rule) */
const ICON_COLOR = "#007bff";

const FEATURE_ICONS = {
  bothWays: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke={ICON_COLOR}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="17 5 21 9 17 13" />
      <path d="M21 9H8a4 4 0 0 0-4 4" />
      <polyline points="7 19 3 15 7 11" />
      <path d="M3 15h13a4 4 0 0 0 4-4" />
    </svg>
  ),
  shield: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke={ICON_COLOR}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  signal: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke={ICON_COLOR}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12h3l2.5 6 5-13 2.5 7H21" />
    </svg>
  ),
};

/* Static Allocation Signal preview card (the product teaser, light-only). */
const AllocationSignalCard = () => (
  <div className="bn-paywall__signal-card">
    <div className="bn-paywall__signal-card-header">
      <span className="bn-paywall__signal-card-title">Allocation Signal</span>
      <span
        className="bn-paywall__signal-card-chip"
        style={{ color: BNP.biti, background: `${BNP.biti}1f` }}
      >
        SHORT
      </span>
    </div>

    <div className="bn-paywall__signal-card-copy">
      <strong>Partial short maintained.</strong>{" "}
      <span>
        Bitcoin signals stay bearish, so the model holds the inverse ETF with
        the majority parked in cash.
      </span>
    </div>

    <div className="bn-paywall__signal-card-bar">
      {BNP_PREVIEW_ALLOCATION.filter((entry) => entry.weight > 0).map(
        (entry) => (
          <div
            key={entry.ticker}
            style={{ width: `${entry.weight}%`, background: entry.color }}
          />
        ),
      )}
    </div>

    <div className="bn-paywall__signal-card-legend">
      {BNP_PREVIEW_ALLOCATION.map((entry) => (
        <span
          key={entry.ticker}
          className="bn-paywall__signal-card-legend-item"
          style={{ opacity: entry.weight === 0 ? 0.45 : 1 }}
        >
          <span
            className="bn-paywall__signal-card-legend-dot"
            style={{ background: entry.color }}
          />
          <span className="bn-paywall__signal-card-legend-ticker">
            {entry.ticker}
          </span>
          <span className="bn-paywall__signal-card-legend-weight">
            {entry.weight.toFixed(1)}%
          </span>
        </span>
      ))}
    </div>

    <div className="bn-paywall__signal-card-footer">
      Re-evaluated daily by the RL ensemble
    </div>
  </div>
);

/**
 * Bitcoin Navigator — no-access / signup page. The paywall design from the
 * marketing prototype paired with the standard wealth-series contact flow:
 * the CTA opens the shared sales ContactForm, requests are rate-limited to
 * one per week via userSettings, and entitled users are redirected straight
 * to the dashboard.
 */
const BitcoinNavigatorPaywall = ({ userSettings }) => {
  const [open, setOpen] = useState(false);
  const { hasWealthSeriesNavigator, hasWealthSeriesNavigatorAuto, loading } =
    useAuth();
  const history = useHistory();
  const handleClose = () => setOpen(false);

  // Within 1 week of last submit → show disabled state "Access Requested"
  const hasSubmitted = useMemo(() => {
    const submitDate = userSettings?.wealthSeriesBitcoinNavigatorContact;
    const weekSinceSubmit = moment(submitDate).add(1, "week");
    const today = moment();
    return submitDate ? today.isBefore(weekSinceSubmit) : false;
  }, [userSettings, loading]);

  // If user already has access, redirect to the dashboard
  useEffect(() => {
    if (loading) return;
    if (hasWealthSeriesNavigator || hasWealthSeriesNavigatorAuto) {
      history.push("/wealth-series/bitcoin-navigator");
    }
  }, [loading, hasWealthSeriesNavigator, hasWealthSeriesNavigatorAuto]);

  return (
    <div className="bn-paywall">
      {/* Backdrop art + light mask live on the layout's .main-content via the
          wealth-series-bitcoin-navigator-signup body class (Core's pattern),
          so the artwork spans the full page behind the header. */}

      {/* Hero row */}
      <div className="bn-paywall__hero">
        <div className="bn-paywall__hero-grid">
          <div className="bn-paywall__copy">
            <Reveal delay={0.04}>
              <BitcoinNavigatorWordmark size="lg" />
            </Reveal>

            <Reveal delay={0.1}>
              <h1 className="bn-paywall__headline">{BN_PAYWALL_HEADLINE}</h1>
            </Reveal>

            <Reveal delay={0.16}>
              <p className="bn-paywall__body">{BN_PAYWALL_BODY}</p>
            </Reveal>

            <Reveal delay={0.22}>
              <div className="bn-paywall__cta-row">
                {loading ? (
                  <BeautifulSkeleton width={180} height={48} />
                ) : (
                  <Button
                    variant="contained"
                    className="bn-paywall__cta"
                    onClick={() => setOpen(true)}
                    disabled={hasSubmitted}
                  >
                    {hasSubmitted ? "Access Requested" : "Contact Us"}
                  </Button>
                )}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.3} className="bn-paywall__card-col">
            <AllocationSignalCard />
          </Reveal>
        </div>
      </div>

      {/* Features + disclaimer */}
      <div className="bn-paywall__lower">
        <div className="bn-paywall__features">
          {BN_PAYWALL_FEATURES.map((feature, index) => (
            <Reveal key={feature.title} delay={0.34 + index * 0.06}>
              {FEATURE_ICONS[feature.icon]}
              <h3 className="bn-paywall__feature-title">{feature.title}</h3>
              <p className="bn-paywall__feature-body">{feature.body}</p>
            </Reveal>
          ))}
        </div>

        <p className="bn-paywall__disclaimer">{BN_PAYWALL_DISCLAIMER}</p>
      </div>

      <Modal open={open}>
        <ContactForm
          product_type="WEALTH SERIES BITCOIN NAVIGATOR"
          onClose={handleClose}
        />
      </Modal>
    </div>
  );
};

const stateToProps = (state) => ({
  userSettings: state.userSettings.userSettings,
});

export default connect(stateToProps)(BitcoinNavigatorPaywall);
